import axios from "axios";
import store from './../store';
import { Auth } from "./AuthService";
import * as Sentry from "@sentry/capacitor";
const apiVersionUri = `/api/v1`;
const api = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL + apiVersionUri,
    timeout: process.env.VUE_APP_API_TIMEOUT,
    headers: {
        "Content-Type": "application/json",
    },
    transformRequest: [
        function (data, headers) {
            try {
                const tokenResponse = localStorage.getItem('token_response');
                const accessToken = JSON.parse(tokenResponse || '')?.access_token;
                headers.Authorization = 'Bearer ' + accessToken;
            }
            catch (e) {
                Sentry.captureException(e);
            }
            return JSON.stringify(data);
        },
    ],
});
api.interceptors.request.use(config => {
    const cloudApiUrl = localStorage.getItem('cloudApiUrl')
        || store.state.user.cloudApiUrl;
    Sentry.addBreadcrumb({
        category: 'axios',
        message: 'Outgoing request',
        level: 'info',
        data: {
            url: config.url,
            baseURL: cloudApiUrl + apiVersionUri,
            method: config.method
        }
    });
    if (cloudApiUrl)
        config.baseURL = cloudApiUrl + apiVersionUri;
    return config;
});
let isRefreshing = false;
let failedQueue = [];
api.interceptors.response.use(response => response, async (error) => {
    const originalRequest = error.config;
    if (!error.response) {
        console.log(error);
        error.response = { data: { message: 'Etwas ist schief gelaufen. Bitte versuche es erneut.' } };
        return Promise.reject(error);
    }
    if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        Sentry.addBreadcrumb({
            category: 'auth',
            message: '401 Intercepted',
            level: 'warning',
            data: { url: originalRequest.url }
        });
        if (isRefreshing) {
            Sentry.addBreadcrumb({
                category: 'auth',
                message: 'Token refresh in progress → pushing request to queue',
                level: 'info',
                data: { queueLength: failedQueue.length }
            });
            return new Promise((resolve, reject) => {
                failedQueue.push({
                    resolve: (token) => {
                        Sentry.addBreadcrumb({
                            category: 'auth',
                            message: 'Retrying queued request',
                            level: 'info',
                            data: { url: originalRequest.url }
                        });
                        originalRequest.headers['Authorization'] = 'Bearer ' + token;
                        resolve(api(originalRequest));
                    },
                    reject: (err) => reject(err)
                });
            });
        }
        isRefreshing = true;
        try {
            Sentry.addBreadcrumb({
                category: 'auth',
                message: 'Refreshing token…',
                level: 'info'
            });
            await Auth.Instance.refreshToken();
            const tokenResponse = JSON.parse(localStorage.getItem("token_response") || '{}');
            const accessToken = tokenResponse?.access_token;
            processFailedRequests();
            originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
            return api(originalRequest);
        }
        catch (err) {
            Sentry.captureMessage("Token refresh FAILED", {
                level: 'error',
                extra: { err }
            });
            processFailedRequests(err);
            return Promise.reject(err);
        }
        finally {
            isRefreshing = false;
        }
    }
    Sentry.captureMessage("Axios request failed (non-401)", {
        level: 'error',
        extra: {
            url: originalRequest.url,
            status: error.response.status,
            errorData: error.response.data
        }
    });
    return Promise.reject(error);
});
function processFailedRequests(error) {
    failedQueue.forEach((promise) => {
        if (error) {
            promise.reject(error);
        }
        else {
            promise.resolve();
        }
    });
    failedQueue = [];
}
export default api;
